/**
 * adminAcademicResourceStore — Academic Resources slice of the admin store
 * (Zustand). Exposes the admin Content endpoints for books, courses and
 * journals, which back the "Academic Resources" page.
 *
 * Endpoints:
 *   GET    /api/admin/books                         — list (page, limit, search, status, sort)
 *   GET    /api/admin/books/:id                     — detail
 *   PATCH  /api/admin/books/:id                     — edit
 *   PATCH  /api/admin/books/:id/approve             — approve
 *   PATCH  /api/admin/books/:id/reject              — reject ({ reason })
 *
 *   GET    /api/admin/courses                       — list
 *   GET    /api/admin/courses/:id                   — detail
 *   PATCH  /api/admin/courses/:id                   — edit
 *   PATCH  /api/admin/courses/:id/approve           — approve
 *   PATCH  /api/admin/courses/:id/reject            — reject ({ reason })
 *   PATCH  /api/admin/courses/:id/disable           — disable
 *   PATCH  /api/admin/courses/:id/feature           — feature
 *   PATCH  /api/admin/courses/:id/unfeature         — unfeature
 *
 *   GET    /api/admin/journals                      — list
 *   GET    /api/admin/journals/:id                  — detail
 *   PATCH  /api/admin/journals/:id                  — edit
 *   PATCH  /api/admin/journals/:id/approve          — approve
 *   PATCH  /api/admin/journals/:id/reject           — reject ({ reason })
 */
import { API_URL } from "../../constants";
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";

const DEFAULT_PAGINATION = { total: 0, totalPages: 1 };

export const academicResourceInitialState = {
  // Books
  books: [],
  booksLoading: false,
  booksError: null,
  booksTotal: 0,
  booksPages: 1,
  bookDetail: null,
  bookDetailLoading: false,
  bookDetailError: null,

  // Courses
  courses: [],
  coursesLoading: false,
  coursesError: null,
  coursesTotal: 0,
  coursesPages: 1,
  courseDetail: null,
  courseDetailLoading: false,
  courseDetailError: null,

  // Journals
  journals: [],
  journalsLoading: false,
  journalsError: null,
  journalsTotal: 0,
  journalsPages: 1,
  journalDetail: null,
  journalDetailLoading: false,
  journalDetailError: null,

  // Pending moderation count (badge on the sidebar)
  pendingModerationCount: 0,
  pendingModerationLoading: false,

  // Mutations (approve / reject / edit / disable / feature)
  academicMutationLoading: false,
  academicMutationError: null,
};

/**
 * Normalize the varying list shapes the content endpoints may return into
 * `{ items, total, totalPages }`.
 */
function normalizeListPayload(data) {
  if (!data || typeof data !== "object") return { items: [], ...DEFAULT_PAGINATION };

  let items = [];
  if (Array.isArray(data)) {
    items = data;
  } else if (Array.isArray(data.data)) {
    items = data.data;
  } else {
    const payload = data.data && typeof data.data === "object" ? data.data : data;
    items = payload.items || payload.books || payload.courses || payload.journals || data.items || [];
  }

  const pagination = data.pagination || {};
  return {
    items: Array.isArray(items) ? items : [],
    total: pagination.total ?? data.total ?? (Array.isArray(items) ? items.length : 0),
    totalPages: pagination.totalPages ?? pagination.pages ?? data.totalPages ?? data.pages ?? 1,
  };
}

/**
 * Thin authenticated JSON fetch that surfaces the API error envelope
 * (`{ error: { message } }`) as a thrown Error with a displayable message.
 */
async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const looksJson = contentType.includes("application/json") ||
      text.trim().startsWith("{") || text.trim().startsWith("[");
    if (looksJson) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    const message = toDisplayString(
      data?.error ?? data?.message,
      `Request failed (${response.status})`
    );
    throw new Error(message);
  }

  return data;
}

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Single source of truth for the three resource kinds.
const KINDS = [
  { singular: "book", plural: "books", path: "/api/admin/books" },
  { singular: "course", plural: "courses", path: "/api/admin/courses" },
  { singular: "journal", plural: "journals", path: "/api/admin/journals" },
];

export function createAcademicResourceActions(set, get) {
  const actions = {};

  /**
   * Run an authenticated mutation, flip the shared mutation flags and return
   * `{ success, data?, error? }`. On success the affected list item is updated
   * in place when possible so the UI reflects the change without a refetch.
   */
  async function mutate({ path, method, body, successMessage, plural, singular, id, patch }) {
    set({ academicMutationLoading: true, academicMutationError: null });
    try {
      const data = await apiRequest(get, path, { method, body });
      const payload = data?.data ?? data;
      const state = { academicMutationLoading: false, academicMutationError: null, successMessage };

      if (plural && id !== undefined) {
        const list = get()[plural] || [];
        state[plural] = list.map((item) =>
          String(item.id) === String(id)
            ? { ...item, ...(patch || {}), ...(payload && typeof payload === "object" ? payload : {}) }
            : item
        );
      }

      // Keep the open detail page in sync immediately; the page may also
      // refetch in the background, but the status flips as soon as the
      // mutation succeeds.
      const detailKey = singular ? `${singular}Detail` : null;
      const detail = detailKey ? get()[detailKey] : null;
      if (detailKey && detail && String(detail.id) === String(id)) {
        state[detailKey] = {
          ...detail,
          ...(patch || {}),
          ...(payload && typeof payload === "object" ? payload : {}),
        };
      }

      set(state);
      return { success: true, data: payload };
    } catch (err) {
      const msg = toDisplayString(err.message, "Action failed");
      set({ academicMutationLoading: false, academicMutationError: msg });
      return { success: false, error: msg };
    }
  }

  for (const { singular, plural, path } of KINDS) {
    const CapS = cap(singular);
    const CapP = cap(plural);

    // GET /api/admin/<plural>
    actions[`fetch${CapP}`] = async (params = {}) => {
      set({ [`${plural}Loading`]: true, [`${plural}Error`]: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(get, `${path}${query ? `?${query}` : ""}`);
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          [`${plural}Loading`]: false,
          [plural]: items,
          [`${plural}Total`]: total,
          [`${plural}Pages`]: totalPages,
        });
        return { success: true, items, total, totalPages };
      } catch (err) {
        const msg = toDisplayString(err.message, `Failed to fetch ${plural}`);
        set({ [`${plural}Loading`]: false, [`${plural}Error`]: msg });
        return { success: false, error: msg };
      }
    };

    // GET /api/admin/<plural>/:id
    actions[`fetch${CapS}`] = async (id) => {
      set({ [`${singular}DetailLoading`]: true, [`${singular}DetailError`]: null });
      try {
        const data = await apiRequest(get, `${path}/${id}`);
        const detail = data?.data ?? data?.[singular] ?? data;
        set({ [`${singular}DetailLoading`]: false, [`${singular}Detail`]: detail });
        return { success: true, data: detail };
      } catch (err) {
        const msg = toDisplayString(err.message, `Failed to fetch ${singular}`);
        set({ [`${singular}DetailLoading`]: false, [`${singular}DetailError`]: msg });
        return { success: false, error: msg };
      }
    };

    // PATCH /api/admin/<plural>/:id
    actions[`update${CapS}`] = (id, payload = {}) =>
      mutate({
        path: `${path}/${id}`,
        method: "PATCH",
        body: payload,
        plural,
        singular,
        id,
        patch: payload,
        successMessage: `${CapS} updated successfully`,
      });

    // PATCH /api/admin/<plural>/:id/approve
    actions[`approve${CapS}`] = (id) =>
      mutate({
        path: `${path}/${id}/approve`,
        method: "PATCH",
        body: undefined,
        plural,
        singular,
        id,
        patch: { status: "APPROVED", rejectionReason: null },
        successMessage: `${CapS} approved successfully`,
      });

    // PATCH /api/admin/<plural>/:id/reject
    actions[`reject${CapS}`] = (id, reason = "") =>
      mutate({
        path: `${path}/${id}/reject`,
        method: "PATCH",
        body: { reason },
        plural,
        singular,
        id,
        patch: { status: "REJECTED", rejectionReason: reason || null },
        successMessage: `${CapS} rejected successfully`,
      });
  }

  // Course-only lifecycle actions.
  actions.disableCourse = (id) =>
    mutate({
      path: `/api/admin/courses/${id}/disable`,
      method: "PATCH",
      body: {},
      plural: "courses",
      singular: "course",
      id,
      patch: { status: "DISABLED" },
      successMessage: "Course disabled successfully",
    });

  actions.featureCourse = (id) =>
    mutate({
      path: `/api/admin/courses/${id}/feature`,
      method: "PATCH",
      body: {},
      plural: "courses",
      singular: "course",
      id,
      patch: { isFeatured: true },
      successMessage: "Course featured successfully",
    });

  actions.unfeatureCourse = (id) =>
    mutate({
      path: `/api/admin/courses/${id}/unfeature`,
      method: "PATCH",
      body: {},
      plural: "courses",
      singular: "course",
      id,
      patch: { isFeatured: false },
      successMessage: "Course unfeatured successfully",
    });

  actions.deleteCourse = async (id) => {
    set({ academicMutationLoading: true, academicMutationError: null });
    try {
      await apiRequest(get, `/api/admin/courses/${encodeURIComponent(id)}`, { method: "DELETE" });
      set((state) => ({
        academicMutationLoading: false,
        academicMutationError: null,
        courses: (state.courses || []).filter((course) => String(course.id) !== String(id)),
        courseDetail: state.courseDetail && String(state.courseDetail.id) === String(id) ? null : state.courseDetail,
        successMessage: "Course deleted successfully",
      }));
      return { success: true };
    } catch (err) {
      const message = toDisplayString(err.message, "Failed to delete course");
      set({ academicMutationLoading: false, academicMutationError: message });
      return { success: false, error: message };
    }
  };

  // Sum of PENDING books + courses + journals, used by the sidebar badge so the
  // admin can see at a glance how many resources await approval.
  actions.fetchPendingModerationCount = async () => {
    set({ pendingModerationLoading: true });
    try {
      const counts = await Promise.all(
        KINDS.map(async ({ path }) => {
          const query = new URLSearchParams({
            status: "PENDING",
            page: "1",
            limit: "1",
          }).toString();
          const data = await apiRequest(get, `${path}?${query}`);
          const { total } = normalizeListPayload(data);
          return total;
        })
      );
      const pendingModerationCount = counts.reduce((sum, value) => sum + (value || 0), 0);
      set({ pendingModerationLoading: false, pendingModerationCount });
      return { success: true, count: pendingModerationCount };
    } catch (err) {
      set({ pendingModerationLoading: false });
      return { success: false, error: toDisplayString(err.message, "Failed to load pending count") };
    }
  };

  return actions;
}

const academicResourceActions = createAcademicResourceActions;
export default academicResourceActions;

